import type { IEvent, IShift, IShiftDetail } from "@/data/model/Booking";
import { Weekday } from "@/data/model/Enums";
import type { IEventsQry } from "@/data/queries/booking/EventsQry";
import type { IShiftsQry } from "@/data/queries/booking/ShiftsQry";
import { DateTime } from "@/data/util/DateTime";

import {
    BaseServiceDao,
    type IEditableDataSource,
    type IPaginatedResult,
    parseCollection,
    parsePaginatedResult,
} from "./BaseServiceDao";

export class BookingDao extends BaseServiceDao {
    public static className = "BookingDao";

    constructor() {
        super("booking");
        this.isLocationDependent = true;
    }

    public async getShift(id: string): Promise<IShiftDetail> {
        return this.get("shift/" + id, undefined, BookingDao.parseShiftDetail);
    }

    public async getShifts(qry: IShiftsQry, abortSignal?: AbortSignal): Promise<IPaginatedResult<IShift>> {
        return this.get("shift/", qry, (l) => parsePaginatedResult(l, BookingDao.parseShift), abortSignal);
    }

    public async getEvent(id: string): Promise<IEvent> {
        return this.get("event/" + id, undefined, BookingDao.parseEvent);
    }

    public async getEvents(qry: IEventsQry, abortSignal?: AbortSignal): Promise<IPaginatedResult<IEvent>> {
        return this.get("event/", qry, (l) => parsePaginatedResult(l, BookingDao.parseEvent), abortSignal);
    }

    public shiftDataSource: IEditableDataSource<IShift, IShiftDetail> = {
        getData: (filter, abortSignal) => this.getShifts(filter, abortSignal),
        getSingle: (m) => this.getShift(m),
        create: undefined as any,
        update: undefined as any,
        remove: undefined as any,
    };

    public eventDataSource: IEditableDataSource<IEvent> = {
        getData: (filter, abortSignal) => this.getEvents(filter, abortSignal),
        getSingle: (m) => this.getEvent(m),
        create: undefined as any,
        update: undefined as any,
        remove: undefined as any,
    };

    public static parseShift(literal: any): IShift {
        return {
            id: literal.id,
            name: literal.name,
            weekdays:
                literal.weekdays == null ? [] : parseCollection<Weekday>(literal.weekdays, (c) => (Weekday as any)[c]),
            dateFrom: literal.dateFrom != null ? DateTime.parseIsoDate(literal.dateFrom) : undefined,
            dateUntil: literal.dateUntil != null ? DateTime.parseIsoDate(literal.dateUntil) : undefined,
            timeFrom: literal.timeFrom != null ? literal.timeFrom : undefined,
            timeUntil: literal.timeUntil != null ? literal.timeUntil : undefined,
        };
    }

    public static parseShiftDetail(literal: any): IShiftDetail {
        const b: IShiftDetail = this.parseShift(literal) as any;

        b.bookingDeadline = literal.bookingDeadline;
        b.capacitySlots = parseCollection(literal.capacitySlots, (a) => {
            return {
                id: a.id,
                isLocked: a.isLocked,
                slotStart: a.slotStart,
                capacity: a.capacity != null ? parseInt(a.capacity) : undefined,
            };
        });

        b.maxSeats = literal.maxSeats != null ? parseInt(literal.maxSeats) : undefined;
        b.slotDuration = literal.slotDuration;
        b.stayDuration = literal.stayDuration != null ? literal.stayDuration : undefined;

        return b;
    }

    public static parseEvent(literal: any): IEvent {
        return {
            id: literal.id,
            start: DateTime.parseIsoDate(literal.start),
            end: DateTime.parseIsoDate(literal.end),
            nameDe: literal.nameDe == null ? undefined : literal.nameDe,
            nameFr: literal.nameFr == null ? undefined : literal.nameFr,
            nameEn: literal.nameEn == null ? undefined : literal.nameEn,
            nameIt: literal.nameIt == null ? undefined : literal.nameIt,
        };
    }
}
